import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import { CssBaseline, type PaletteMode } from '@mui/material';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createCustomTheme } from '@static/styles/theme';

const isPaletteMode = (mode?: string): mode is PaletteMode => {
  return mode === 'dark' || mode === 'light';
};

const getStoredPaletteMode = (): PaletteMode => {
  const storedMode = localStorage.getItem('ThemeStore@paletteMode') || undefined;

  return isPaletteMode(storedMode) ? storedMode : 'light';
};

interface ThemeProviderContextValue {
  paletteMode: PaletteMode;
  togglePaletteMode: (mode?: PaletteMode) => void;
}

const ThemeProviderContext = createContext<ThemeProviderContextValue | null>(null);

export const useThemeProvider = () => {
  const context = useContext(ThemeProviderContext);

  if (!context) throw new Error('useThemeProvider can only be used inside a ThemeProvider');

  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [paletteMode, setPaletteMode] = useState<PaletteMode>(getStoredPaletteMode());

  const togglePaletteMode = useCallback(
    (mode?: PaletteMode) => {
      if (mode) setPaletteMode(mode);
      else setPaletteMode(paletteMode === 'light' ? 'dark' : 'light');
    },
    [paletteMode],
  );

  const theme = useMemo(() => {
    return createCustomTheme(paletteMode);
  }, [paletteMode]);

  useEffect(() => {
    localStorage.setItem('ThemeStore@paletteMode', paletteMode);
  }, [paletteMode]);

  const value = useMemo(
    () => ({ paletteMode, togglePaletteMode: togglePaletteMode }),
    [paletteMode, togglePaletteMode],
  );

  return (
    <ThemeProviderContext.Provider value={value}>
      <CssBaseline enableColorScheme />
      <GlobalStyles
        styles={`
          body {
            background-color: ${theme.palette.background.default};
          }
        `}
      />
      <MUIThemeProvider theme={theme}>{children}</MUIThemeProvider>
    </ThemeProviderContext.Provider>
  );
};
